/* eslint-disable react/no-array-index-key */
import { useTranslation } from 'react-i18next';
import {
  useCallback, useMemo, useState, useEffect, useRef,
} from 'react';
import { useLocation, useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import STRING_KEYS from '../../../../../../language/keys';
import { ConversationGroupsEnum } from '../../../../../../enums/outreach/conversation-groups.enum';
import { usePageFilters } from '../../../../../../hooks/use-page-filters.hooks';
import { AppRouting } from '../../../../../../services/appRoutingResolver';
import Portal from '../../../../../../components/Common/Portal/portal.commponent';
import { getConversationGroupOrder, getGroupIcon } from '../conversation-groups.util';
import { PageName } from '../../../../../../enums/page-name.enum';
import { SequencePreferencesDto } from '../../../../../../store/rtk-query/apis/sequence/dto/sequence-preferences.dto';
import { useGetPositionSequencePreferencesSelector } from '../../../../../../store/rtk-query/apis/sequence/selectors/position-sequence-preferences.selector';
import { SequenceModeEnum } from '../../../../../../store/rtk-query/apis/sequence/enums/sequence-mode.enum';
import { ErrorIcon } from '../conversation-groups.styled';
import { useConversationGroupsInfinite } from '../conversation-groups-infinite.context';
import { ErrorSnackBar } from '../../../../../../components/Common/CustomSnackBar/ErrorSnackBar';
import {
  ConversationGroupsContainer,
  ConversationNumber,
  GroupContainer,
  GroupsContainer,
  GroupTitleContainer,
  HeaderContainer,
  HeaderTitle,
  ProgressContainer,
} from './conversation-group.styled';
import { ConversationGroupProps } from './conversation-group';
import ConversationItems from './conversation-items/conversation-items.component';

export const CONVERSATION_ITEMS_ID = 'CONVERSATION_ITEMS';

const ConversationGroup = ({ conversationGroups }: ConversationGroupProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { positionId } = useParams<MatchUrlParams>();
  const [showConversationsError, setShowConversationsError] = useState<boolean>(false);

  const {
    getFiltersByKey,
    isFilterExists,
    setFilterByKey,
    deleteFiltersByKey,
  } = usePageFilters();

  const selectedConversationGroupName = useMemo<string>(() => {
    return getFiltersByKey(AppRouting.URL_PARAMS.OUTREACH.CONVERSATION_GROUP)[0];
  }, [getFiltersByKey]);

  const defaultSetRef = useRef(false);

  const {
    data: { sequencePreferences = {} as SequencePreferencesDto },
    isSuccess: isSequencePreferencesSuccess,
  } = useGetPositionSequencePreferencesSelector(positionId);
  const { mode } = sequencePreferences;
  const isAutoMode = mode === SequenceModeEnum.Auto || mode === SequenceModeEnum.Custom;

  const { groupedConversations, loadMore, isError } = useConversationGroupsInfinite();
  
  useEffect(() => {
    if (!selectedConversationGroupName && !defaultSetRef.current && isSequencePreferencesSuccess) {
      const sortedConversationGroups = getConversationGroupOrder(conversationGroups, isAutoMode);
      const firstNonEmptyGroup = sortedConversationGroups.find((group) => group.count > 0);
      if (firstNonEmptyGroup) {
        setFilterByKey(AppRouting.URL_PARAMS.OUTREACH.CONVERSATION_GROUP, firstNonEmptyGroup.groupName, false);
      }
      defaultSetRef.current = true;
    }
  }, [conversationGroups, selectedConversationGroupName, setFilterByKey, isAutoMode, isSequencePreferencesSuccess]);

  const onSelectGroupClick = useCallback((step: ConversationGroupsEnum, itemDisabled: boolean) => {
    if (!itemDisabled) {
      const pageName = AppRouting.getPageName(location.pathname);
      if (pageName !== PageName.Conversation && pageName !== PageName.ConversationsLoading) {
        history.push(AppRouting.buildPositionConversationPageUrl(positionId, step));
      } else if (!isFilterExists(AppRouting.URL_PARAMS.OUTREACH.CONVERSATION_GROUP, step)) {
        setFilterByKey(AppRouting.URL_PARAMS.OUTREACH.CONVERSATION_GROUP, step, false);
      } else {
        deleteFiltersByKey(AppRouting.URL_PARAMS.OUTREACH.CONVERSATION_GROUP);
      }
    }
  }, [location.pathname, isFilterExists, history, positionId, setFilterByKey, deleteFiltersByKey]);

  const allConversationsLength =  conversationGroups.find(({ groupName }) => groupName === ConversationGroupsEnum.All)?.count || 0;

  const sortedConversationGroups = useMemo(() => {
    return getConversationGroupOrder(conversationGroups, isAutoMode);
  }, [conversationGroups, isAutoMode]);

  const Groups = useMemo(() => sortedConversationGroups.map(({ groupName, count }) => {
    const widthPercentage = groupName === ConversationGroupsEnum.All ? 100 : (count / allConversationsLength) * 100;
    const itemDisabled = !count;
    const getTranslatedGroupName = () => {
      if (groupName.includes(ConversationGroupsEnum.Step)) {
        const [, stepNumber] = groupName.split('_');

        return translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.GROUP_NAME.STEP, { stepNumber });
      }

      return translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.GROUP_NAME[groupName]);
    };

    const groupData = groupedConversations[groupName];
    const items = groupData?.conversations ?? [];
    const bounceExists = items?.some((item) => item.latestMessage?.wasBounced);

    return (
      <GroupContainer
        active={ groupName === selectedConversationGroupName }
        key={ groupName }
        onClick={ () => onSelectGroupClick(groupName as ConversationGroupsEnum, itemDisabled) }
        disabled={ itemDisabled }
      >
        <ProgressContainer active={ groupName === selectedConversationGroupName } widthPercentage={ widthPercentage } />
        <GroupTitleContainer>
          { getGroupIcon(groupName) }
          { getTranslatedGroupName() }
        </GroupTitleContainer>
        {
          groupName !== ConversationGroupsEnum.All && bounceExists ?
            <ErrorIcon /> :
            <ConversationNumber>{ count }</ConversationNumber>
        }
      </GroupContainer>
    );
  }), [sortedConversationGroups, allConversationsLength, groupedConversations, selectedConversationGroupName, translate, onSelectGroupClick]);

  useEffect(() => {
    if (isError) {
      setShowConversationsError(true);
    }
  }, [isError]);

  return (
    <div>
      <ConversationGroupsContainer>
        <HeaderContainer>
          <HeaderTitle>{ translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.SECTION_TITLE) }</HeaderTitle>
        </HeaderContainer>
        <GroupsContainer>
          { Groups }
        </GroupsContainer>
      </ConversationGroupsContainer>
      <Portal containerId={ CONVERSATION_ITEMS_ID } removeAfterUnmount={ false }>
        <ConversationItems
          isAutoMode={ isAutoMode }
          selectedConversationGroupName={ selectedConversationGroupName }
          conversations={ groupedConversations[selectedConversationGroupName]?.conversations || [] }
          loadMore={ loadMore }
          isLoading={ groupedConversations[selectedConversationGroupName]?.isLoading }
          isFetching={ groupedConversations[selectedConversationGroupName]?.isFetching }
        />
      </Portal>
      <ErrorSnackBar
        title={ translate(STRING_KEYS.SNACKBAR.OUTREACH_CONVERSATIONS_FAILED) }
        isOpen={ showConversationsError }
        actionTitle={ translate(STRING_KEYS.TRY_AGAIN) }
        onActionClick={ () => setShowConversationsError(false) }
        onClose={ () => setShowConversationsError(false) }
        shouldAutoDismiss
      />
    </div>
  );
};

export default ConversationGroup;
